import { toaster } from '@/shared/ui/toaster.component';
import { formatCurrency, formatDateToBrazil } from '@/shared/utils';
import {
  Button,
  Dialog,
  Field,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { useEffect, useState } from 'react';
import type { PolicyCharge } from '../../../services/policy-charges';
import { useCreateManualPolicyCharge } from '../hooks';

interface ManualChargeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  policyId: string;
  charge?: PolicyCharge;
}

/**
 * Cobrança manual com valor informado — a saída para a parcela vencida.
 *
 * O cálculo de juros e multa é do financeiro, fora do sistema: a régua é do
 * I4Pro e o link só transporta o valor final. Por isso o campo é livre e a
 * justificativa é obrigatória — ela é o que fica na trilha de auditoria junto
 * com o nome de quem pediu.
 */
export function ManualChargeDialog({
  isOpen,
  onClose,
  policyId,
  charge,
}: ManualChargeDialogProps) {
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { mutateAsync: createManual, isPending } =
    useCreateManualPolicyCharge(policyId);

  useEffect(() => {
    if (!isOpen) return;
    // Pré-preenche com o valor original: o caso comum é ajustar para cima, e
    // partir do zero convidaria a erro de digitação.
    setAmount(charge?.amount != null ? String(charge.amount) : '');
    setDueDate(new Date().toISOString().split('T')[0]);
    setReason('');
    setErrors({});
  }, [isOpen, charge?.amount]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    const parsedAmount = Number(amount.replace(',', '.'));

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      next.amount = 'Informe um valor maior que zero';
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
      next.dueDate = 'Informe o novo vencimento';
    }
    if (!reason.trim()) {
      next.reason = 'A justificativa é obrigatória';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // A cobrança única do cartão vive na parcela 0, que a API não aceita como
  // alvo: ela responde pela primeira parcela que cobre.
  const isSingleCharge = charge?.scope === 'POLICY';
  const targetInstallment = isSingleCharge
    ? (charge?.coveredInstallments?.[0] ?? charge?.installmentNumber)
    : charge?.installmentNumber;
  const chargeLabel = isSingleCharge
    ? 'cobrança única'
    : `parcela ${charge?.installmentNumber}`;

  const handleSubmit = async () => {
    if (!charge || targetInstallment == null || !validate()) return;

    try {
      await createManual({
        installmentNumber: targetInstallment,
        amount: Number(amount.replace(',', '.')),
        dueDate,
        reason: reason.trim(),
      });

      toaster.create({
        type: 'success',
        title: 'Cobrança gerada',
        description: `Novo link criado para a ${chargeLabel}.`,
      });
      onClose();
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao gerar a cobrança',
        description:
          error instanceof Error
            ? error.message
            : 'Tente novamente em instantes.',
      });
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={details => !details.open && onClose()}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-full max-w-lg'>
          <Dialog.Header>
            <Dialog.Title>
              Gerar cobrança manual
              {charge ? ` — ${chargeLabel}` : ''}
            </Dialog.Title>
            <Dialog.CloseTrigger asChild>
              <Dialog.CloseButton />
            </Dialog.CloseTrigger>
          </Dialog.Header>

          <Dialog.Body>
            {charge && (
              <Text className='mb-4 text-sm text-alba-gray-600'>
                Valor original {formatCurrency(charge.amount ?? 0)}, vencido em{' '}
                {formatDateToBrazil(charge.dueDate)}. Informe o valor final, já
                com juros e multa calculados.
              </Text>
            )}

            <div className='flex flex-col gap-4'>
              <Field.Root invalid={!!errors.amount}>
                <Field.Label>Valor a cobrar (R$)</Field.Label>
                <Field.Input
                  value={amount}
                  onChange={event => setAmount(event.target.value)}
                  placeholder='0,00'
                  inputMode='decimal'
                />
                {errors.amount && (
                  <Field.ErrorText>{errors.amount}</Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.dueDate}>
                <Field.Label>Novo vencimento</Field.Label>
                <Field.Input
                  type='date'
                  value={dueDate}
                  onChange={event => setDueDate(event.target.value)}
                />
                {errors.dueDate && (
                  <Field.ErrorText>{errors.dueDate}</Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.reason}>
                <Field.Label>Justificativa</Field.Label>
                <Field.Input
                  value={reason}
                  onChange={event => setReason(event.target.value)}
                  placeholder='Ex.: juros de 2% + mora de 0,033% a.d. sobre a parcela vencida'
                  maxLength={500}
                />
                {errors.reason ? (
                  <Field.ErrorText>{errors.reason}</Field.ErrorText>
                ) : (
                  <Field.HelperText>
                    Fica registrada com o seu nome no histórico de tentativas.
                  </Field.HelperText>
                )}
              </Field.Root>
            </div>
          </Dialog.Body>

          <Dialog.Footer>
            <Button variant='outline' onClick={onClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} loading={isPending}>
              Gerar link
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
